import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from 'react-router-dom';

import useAuthStore from "store/authStore";
import { canAccess, MenuUrl } from "config/permissions";
import { ArrowLeftRight, Check, Pin, RotateCcw } from "lucide-react";
import {
  ALL_APPS, appEntryUrl, applyAppOrder, moveInOrder, normalizeText, readAppOrder, readAppUsage, readPinnedApps,
  saveAppOrder, savePinnedApps, sortByUsage, visiblePages,
} from "components/SidebarNew/appMenu";
import "./style.scss";
import { Header } from "./component/Header";
import { Footer, iconUrl, useWeather } from "./component/Footer";
import { ChatBot } from "./component/ChatBot";
import { AppGrid, AppSlider, type AppTileItem, type MoveApp, type PinControl } from "./component/AppSlider";
import { readDashboardUser } from "./component/user";
import { useClock } from "./component/useClock";
import { LeftPanel, RightPanel, useHomeCtx } from "./home/HomeSides";

const SectionTitle = ({ children }: { children: React.ReactNode }) => (
  <div className="mb-3 flex items-center gap-3 text-[11px] font-semibold uppercase tracking-wider text-slate-400 md:mb-4">
    <span className="h-px flex-1 bg-white/10" />
    <span className="flex flex-wrap items-center justify-center gap-1.5">{children}</span>
    <span className="h-px flex-1 bg-white/10" />
  </div>
);

const ArrangeToggle = ({ on, onClick }: { on: boolean; onClick: () => void }) => (
  <button
    type="button"
    onClick={onClick}
    className={`ml-1 flex items-center gap-1 rounded-full border px-2 py-0.5 font-normal normal-case tracking-normal transition ${on ? "border-teal-400/60 bg-teal-500/15 text-teal-200" : "border-white/10 text-slate-400 hover:border-white/25 hover:text-slate-200"}`}
  >
    {on ? <><Check className="h-3 w-3" /> Xong</> : <><ArrowLeftRight className="h-3 w-3" /> Sắp xếp</>}
  </button>
);

const ArrangeHint = () => (
  <p className="-mt-1 mb-3 text-center text-[11px] text-slate-500">
    Nhấn giữ ô rồi kéo sang chỗ khác (điện thoại: bấm mũi tên dưới ô) — tự lưu.
  </p>
);

function useNetworkCanvas(canvasRef: React.RefObject<HTMLCanvasElement>) {
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    let width = 0;
    let height = 0;
    let animationId = 0;

    const PARTICLE_COUNT = 100;
    const CONNECTION_DISTANCE = 150;

    class Particle {
      x: number;
      y: number;
      vx: number;
      vy: number;
      radius: number;

      constructor() {
        this.x = Math.random() * width;
        this.y = Math.random() * height;
        this.vx = (Math.random() - 0.5) * 0.5;
        this.vy = (Math.random() - 0.5) * 0.5;
        this.radius = Math.random() * 2 + 1;
      }

      update() {
        this.x += this.vx;
        this.y += this.vy;
        if (this.x < 0 || this.x > width) this.vx = -this.vx;
        if (this.y < 0 || this.y > height) this.vy = -this.vy;
      }

      draw() {
        if (!ctx) return;
        ctx.beginPath();
        ctx.arc(this.x, this.y, this.radius, 0, Math.PI * 2);
        ctx.fillStyle = "rgba(45, 212, 191, 0.5)";
        ctx.fill();
      }
    }

    let particles: Particle[] = [];

    const initParticles = () => {
      particles = Array.from({ length: PARTICLE_COUNT }, () => new Particle());
    };

    const resizeCanvas = () => {
      width = window.innerWidth;
      height = window.innerHeight;
      canvas.width = width;
      canvas.height = height;
      initParticles();
    };

    const animate = () => {
      ctx.clearRect(0, 0, width, height);

      for (let i = 0; i < particles.length; i++) {
        particles[i].update();
        particles[i].draw();

        for (let j = i + 1; j < particles.length; j++) {
          const dx = particles[i].x - particles[j].x;
          const dy = particles[i].y - particles[j].y;
          const distance = Math.sqrt(dx * dx + dy * dy);

          if (distance < CONNECTION_DISTANCE) {
            const opacity = 1 - distance / CONNECTION_DISTANCE;
            ctx.beginPath();
            ctx.moveTo(particles[i].x, particles[i].y);
            ctx.lineTo(particles[j].x, particles[j].y);
            ctx.strokeStyle = `rgba(45, 212, 191, ${opacity * 0.2})`;
            ctx.lineWidth = 1;
            ctx.stroke();
          }
        }
      }
      animationId = requestAnimationFrame(animate);
    };

    resizeCanvas();
    animate();
    window.addEventListener("resize", resizeCanvas);

    return () => {
      window.removeEventListener("resize", resizeCanvas);
      cancelAnimationFrame(animationId);
    };
  }, [canvasRef]);
}

export default function KhothiDashboard(): JSX.Element {

  const canvasRef = useRef<HTMLCanvasElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const [user, setUser] = useState(readDashboardUser);
  const [usage] = useState(readAppUsage);
  const [pins, setPins] = useState(readPinnedApps);
  const [arrange, setArrange] = useState(false);
  const [order, setOrder] = useState(readAppOrder);
  const [arrangeAll, setArrangeAll] = useState(false);
  const clock = useClock();
  const weather = useWeather();

  const { permissions, isSuperAdmin } = useAuthStore();

  useNetworkCanvas(canvasRef);
  const home = useHomeCtx(user);

  // Ctrl/⌘ + K: tìm nhanh ứng dụng hoặc khách hàng
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        searchRef.current?.focus();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // Cập nhật tên/avatar khi thông tin user thay đổi (vd: đổi avatar ở trang cá nhân)
  useEffect(() => {
    const refresh = () => setUser(readDashboardUser());
    window.addEventListener("user-updated", refresh);
    window.addEventListener("storage", refresh);
    return () => {
      window.removeEventListener("user-updated", refresh);
      window.removeEventListener("storage", refresh);
    };
  }, []);

  // Ẩn mục chưa có trang ('/#') và mục người dùng không có quyền truy cập
  const isVisible = (link: MenuUrl) => link !== "/#" && canAccess(link, permissions, isSuperAdmin);

  const keyword = normalizeText(searchTerm);

  // Ô ứng dụng = đúng các app trên menu trái (cùng quyền), app hay dùng đứng đầu. Tìm theo tên app hoặc tên trang con:
  // khớp trang con thì ô mở thẳng trang đó.
  const filteredApps: AppTileItem[] = applyAppOrder(sortByUsage(ALL_APPS.flatMap((app) => {
    const entry = appEntryUrl(app, isVisible);
    if (!entry) return [];
    const pages = visiblePages(app, isVisible);
    const nameHit = normalizeText(app.name).includes(keyword);
    const page = keyword && !nameHit ? pages.find((p) => normalizeText(p.title).includes(keyword)) : undefined;
    if (keyword && !nameHit && !page) return [];
    const subtitle = page ? page.title : pages.map((p) => p.title).join(" · ");
    return [{ app, url: page?.url ?? entry, subtitle }];
  }), usage), order);

  // App đã ghim: hiện riêng ở trên theo thứ tự người dùng sắp, không lặp lại trong slider
  const pinnedApps = pins.flatMap((name) => filteredApps.filter((x) => x.app.name === name));
  const otherApps = filteredApps.filter((x) => !pins.includes(x.app.name));
  const updatePins = (next: string[]) => {
    setPins(next);
    savePinnedApps(next);
  };
  const pin: PinControl = {
    isPinned: (name) => pins.includes(name),
    toggle: (name) => updatePins(pins.includes(name) ? pins.filter((n) => n !== name) : [...pins, name]),
  };
  // Vị trí tính theo các ô đang hiện (app ghim nhưng mất quyền xem giữ ở cuối danh sách)
  const movePinned: MoveApp = (name, to) => updatePins(moveInOrder(pinnedApps.map((x) => x.app.name), name, to, pins));
  const arranging = arrange && pinnedApps.length > 1;

  // "Tất cả ứng dụng": đã tự sắp thì giữ thứ tự đó, chưa thì xếp theo mức dùng
  const updateOrder = (next: string[] | null) => {
    setOrder(next);
    saveAppOrder(next);
  };
  const moveApp: MoveApp = (name, to) => updateOrder(moveInOrder(otherApps.map((x) => x.app.name), name, to, order ?? []));
  const arrangingAll = arrangeAll && otherApps.length > 1;

  const firstName = user.fullname.trim() || user.email.split("@")[0];
  const canSearchCustomer = isVisible("/customer");
  const customerSearchUrl = `/customer?q=${encodeURIComponent(searchTerm.trim())}`;
  const onSearchKey = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Escape") setSearchTerm("");
    if (e.key !== "Enter" || !keyword) return;
    if (filteredApps.length === 1) navigate(filteredApps[0].url);
    else if (!filteredApps.length && canSearchCustomer) navigate(customerSearchUrl);
  };

  const today = new Date();
  const DAY = ["Chủ nhật", "Thứ 2", "Thứ 3", "Thứ 4", "Thứ 5", "Thứ 6", "Thứ 7"];
  const todayLabel = `${DAY[today.getDay()]}, ${String(today.getDate()).padStart(2, "0")}/${String(today.getMonth() + 1).padStart(2, "0")}/${today.getFullYear()}`;
  const w = weather.current;

  return (
    <div className="relative h-screen w-screen flex flex-col overflow-hidden bg-[#0f172a] text-gray-100 font-sans text-sm antialiased">
      {/* Tech Network Animated Background */}
      <canvas ref={canvasRef} className="network-canvas absolute inset-0 w-full h-full z-0 pointer-events-none" />
      <div className="absolute inset-0 z-[1] pointer-events-none bg-[radial-gradient(circle_at_30%_30%,rgba(30,64,175,0.4)_0%,rgba(15,23,42,0.9)_70%)]" />

      {/* Top Navigation Bar */}
      <Header user={user} />

      {/* Trang chủ 3 cột (màn rộng): trái = bảng tin + việc hôm nay · giữa = ứng dụng · phải = cá nhân, chỉ tiêu, tiện ích */}
      <main className="relative z-10 flex-1 min-h-0 w-full overflow-y-auto xl:overflow-hidden custom-scrollbar">
        <div className="mx-auto grid max-w-[1760px] grid-cols-1 gap-4 px-3 pb-6 md:grid-cols-2 md:px-4 xl:h-full xl:grid-cols-[320px_minmax(0,1fr)_320px] xl:gap-6 xl:px-6 xl:pb-0 2xl:grid-cols-[350px_minmax(0,1fr)_350px]">
          <aside className="rail-scroll order-2 xl:order-1 xl:h-full xl:overflow-y-auto xl:py-5">
            <LeftPanel ctx={home} />
          </aside>

          <div className="rail-scroll order-1 md:col-span-2 xl:order-2 xl:col-span-1 xl:h-full xl:overflow-y-auto">
            <div className="max-w-5xl mx-auto pt-4 md:pt-8 xl:pt-10 pb-6 xl:pb-10 flex flex-col items-center">
              <div className="mb-3 hidden xl:flex items-center gap-3 rounded-full border border-white/10 bg-white/[0.04] py-1 pl-4 pr-2 text-xs text-slate-300">
                <span className="text-base font-medium tabular-nums text-white">{clock.time}</span>
                <span className="h-3 w-px bg-white/15" />
                <span>{todayLabel}</span>
                {w && (
                  <>
                    <span className="h-3 w-px bg-white/15" />
                    <span className="flex items-center gap-1 capitalize" title={w.weather[0]?.description}>
                      <img src={iconUrl(w.weather[0]?.icon)} alt="" className="-my-2 h-8 w-8" />
                      <b className="font-semibold text-white">{Math.round(w.main.temp)}°</b>
                      <span className="max-w-[120px] truncate text-slate-400">{w.weather[0]?.description}</span>
                    </span>
                  </>
                )}
              </div>
              <h1 className="text-xl sm:text-2xl md:text-3xl font-light text-white text-center">
                {clock.greeting}{firstName ? <>, <span className="font-semibold">{firstName}</span></> : null}
              </h1>
              <p className="mt-1 text-[11px] md:text-xs text-gray-400 text-center">
                Hôm nay bạn muốn làm gì? Chọn ứng dụng bên dưới hoặc tìm nhanh.
              </p>

              {/* Search Bar */}
              <div className="w-full max-w-2xl mt-4 md:mt-6 mb-6 md:mb-10">
                <label className="relative bg-gray-800/40 rounded-lg border border-gray-700/50 flex items-center px-3 md:px-4 py-2 md:py-2.5 hover:bg-gray-800/60 focus-within:border-teal-400/60 transition cursor-text">
                  <i className="fa-solid fa-magnifying-glass text-gray-400 text-sm mr-3" />
                  <input
                    ref={searchRef}
                    type="text"
                    placeholder={canSearchCustomer ? "Tìm ứng dụng, khách hàng (tên / SĐT)…" : "Tìm kiếm ứng dụng"}
                    onKeyDown={onSearchKey}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="bg-transparent border-none outline-none text-gray-200 w-full placeholder-gray-500 text-sm"
                  />
                  {!searchTerm && <kbd className="hidden md:inline ml-2 whitespace-nowrap rounded border border-gray-600/60 px-1.5 text-[10px] text-gray-500">Ctrl K</kbd>}
                  {searchTerm && (
                    <button type="button" onClick={() => setSearchTerm("")} aria-label="Xoá tìm kiếm" className="text-gray-500 hover:text-white ml-2">
                      <i className="fa-solid fa-xmark text-sm" />
                    </button>
                  )}
                </label>
                {keyword && canSearchCustomer && (
                  <Link to={customerSearchUrl} className="mt-2 flex items-center gap-2 rounded-lg border border-teal-400/30 bg-teal-500/10 px-3 py-2 text-xs text-teal-100 hover:bg-teal-500/20">
                    <i className="fa-solid fa-user" />
                    <span className="min-w-0 flex-1 truncate">Tìm khách hàng “{searchTerm.trim()}”</span>
                    <span className="text-[10px] text-teal-300/70">{filteredApps.length ? "Mở" : "Enter"}</span>
                  </Link>
                )}
              </div>

              {/* Ứng dụng: phần ghim ở trên, còn lại kéo ngang (app hay dùng ở trang đầu); đang tìm thì hiện mọi kết quả */}
              {keyword ? <AppGrid items={filteredApps} pin={pin} /> : (
                <>
                  <section className="mb-6 w-full md:mb-8">
                    <SectionTitle>
                      <Pin className="h-3 w-3" /> Đã ghim
                      <span className="font-normal normal-case tracking-normal text-slate-500">{pinnedApps.length}{pinnedApps.length > 1 && !arranging ? " · kéo ô để đổi chỗ" : ""}</span>
                      {pinnedApps.length > 1 && <ArrangeToggle on={arranging} onClick={() => setArrange(!arranging)} />}
                    </SectionTitle>
                    {arranging && <ArrangeHint />}
                    {pinnedApps.length ? <AppGrid items={pinnedApps} pin={pin} onMove={movePinned} arranging={arranging} /> : (
                      <div className="mx-auto max-w-md rounded-xl border border-dashed border-white/15 px-4 py-3 text-center text-[11px] text-slate-400">
                        Rê chuột vào ứng dụng bên dưới và bấm <Pin className="mx-0.5 inline h-3 w-3" /> để ghim lên đây.
                      </div>
                    )}
                  </section>
                  <section className="w-full">
                    <SectionTitle>
                      Tất cả ứng dụng
                      <span className="font-normal normal-case tracking-normal text-slate-500">{order ? "theo thứ tự của bạn" : "hay dùng xếp trước"}</span>
                      {otherApps.length > 1 && <ArrangeToggle on={arrangingAll} onClick={() => setArrangeAll(!arrangingAll)} />}
                      {order && (
                        <button
                          type="button"
                          onClick={() => updateOrder(null)}
                          className="flex items-center gap-1 rounded-full border border-white/10 px-2 py-0.5 font-normal normal-case tracking-normal text-slate-400 transition hover:border-white/25 hover:text-slate-200"
                        >
                          <RotateCcw className="h-3 w-3" /> Xếp theo mức dùng
                        </button>
                      )}
                    </SectionTitle>
                    {arrangingAll && <ArrangeHint />}
                    {/* Sắp xếp: mở hết thành lưới (kéo chuột trên slider là để lật trang) */}
                    {arrangingAll
                      ? <AppGrid items={otherApps} pin={pin} onMove={moveApp} arranging />
                      : <AppSlider items={otherApps} pin={pin} />}
                  </section>
                </>
              )}

              {filteredApps.length === 0 && (
                <div className="text-gray-400 text-sm py-10 text-center">
                  Không tìm thấy ứng dụng nào khớp “{searchTerm.trim()}”.
                </div>
              )}
            </div>
          </div>

          <aside className="rail-scroll order-3 xl:h-full xl:overflow-y-auto xl:py-5">
            <RightPanel ctx={home} />
          </aside>
        </div>
      </main>

      {/* Bottom Widgets Area */}
      <Footer clock={clock} showTasks={isVisible("/work-report")} weather={weather} />

      {/* Trợ lý AI: góc phải dưới */}
      <ChatBot />
    </div>
  );
}
